import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Settings, MapPin, Wrench } from 'lucide-react';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ComingSoonCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 60px 40px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  text-align: center;
  color: #6b7280;
  
  h2 {
    margin: 0 0 16px;
    color: #374151;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }
  
  p {
    margin: 0 0 24px;
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  text-align: left;
  
  li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: #f9fafb;
    border-radius: 8px;
    color: #374151;
    
    svg {
      color: #ed2024;
      flex-shrink: 0;
    }
  }
`;

const LocationInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  
  .location-name {
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .location-details {
    font-size: 0.875rem;
    color: #0369a1;
  }
`;

const LocationServicesPage: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();

  const handleBack = () => {
    // Navigate back to dealer edit page or dealer dashboard
    navigate('/dealers');
  };

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack}>
          <ArrowLeft />
          Back
        </BackButton>
        <h1>
          <Wrench />
          Service Assignment
        </h1>
      </PageHeader>

      <LocationInfo>
        <div className="location-name">
          <MapPin size={16} />
          Location Services
        </div>
        <div className="location-details">
          Configure which services are available at this location
        </div>
      </LocationInfo>

      <ComingSoonCard>
        <h2>
          <Settings />
          Service Assignment Coming Soon
        </h2>
        <p>
          The service assignment feature is currently under development and will be available soon.
          This will allow you to configure which services are available at each location.
        </p>
        
        <FeatureList>
          <li>
            <Wrench size={20} />
            Assign available services to locations
          </li>
          <li>
            <Settings size={20} />
            Configure service-specific settings
          </li>
          <li>
            <MapPin size={20} />
            Location-based service availability
          </li>
        </FeatureList>
        
        <p>
          Once implemented, you'll be able to control which services customers can book 
          at each of your locations, allowing for specialized service offerings per location.
        </p>
      </ComingSoonCard>
    </PageContainer>
  );
};

export default LocationServicesPage;
